/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    ActionGroup,
    ActionKernelType,
    ResumeFromMyShowsActionKernel,
    WatchFromMyShowsActionKernel,
} from 'client-code/actions';
import { CollectionType, MyShowsItemAttributeType, QuickAction } from 'client-code/myshows';

import { InteractiveComponents } from '../../../core/enums';
import { ContentType } from '../../../core/enums/ContentType';
import { TransitionScreenType } from '../../../core/enums/TransitionScreenType';
import { ServicesClientImpl } from '../../../core/interactors';
import { Log } from '../../../core/logging';
import { KeyManager, ScreenTaskControllerTypes } from '../../../core/navigation';
import type { RootState, TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { TActionList, TMyShowsItemList } from '../../../core/types';
import { MyShowItemType } from '../../../core/types/TMyShowsItemList';
import { ListTypes } from '../../../core/utils';
import { setActiveInteractiveComponent } from '../../interactivelayer/interactiveLayer.reducers';
import { setListEntryFocus, setListEntryPosition } from '../../list/list.actions';
import { onMouseOutList, onMouseOverList } from '../../list/list.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
    showPCPinOverlayWithProgramRating,
} from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    collectionScreenTransition,
    programScreenTransition,
} from '../../screens/navigation/screensnavigation';
import { showWhisper } from '../../whisper/whisper.reducer';
import {
    setMyShowsCaretVisibleAction,
    setMyShowsErrorAction,
    setMyShowsItemsAction,
    setMyShowsItemsLoadingAction,
    setMyShowsPageLoadingAction,
} from '../myshows.actions';
import {
    getItemsListHiglightPosition,
    getMyShowsItems,
    onKeyBackMyShowsOperations,
    onKeyDownMyShowsOperation,
    onKeyLeft,
    onKeyRight,
    onKeyUpMyShowsOperations,
    setLastSelectedItem,
    triggerPlayBackForMyShowsItem,
} from '../myshows.reducers';
import { MyShowsListGenerator } from '../MyShowsListGenerator';

const TAG: string = 'MyShowsScreenNavigation';

const MAX_ITEM_IN_PAGE = 13;

const myShowsItemsObj: TMyShowsItemList = {
    lastSelectedItem: {
        index: 0,
        myShowsItemId: '',
    },
    myShows: [],
    sortOrder: '',
    totalMyShowsCount: 0,
};

export const onKeyBackMyShows = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyBackPcPinOverlay());
        dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
    } else dispatch(onKeyBackMyShowsOperations());
};

export const onKeyUpMyShows = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyUpPcPinOverlay());
    else dispatch(onKeyUpMyShowsOperations());
};

export const onKeyDownMyShows = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyDownPcPinOverlay());
    else dispatch(onKeyDownMyShowsOperation());
};

export const onKeyRightMyShows = () => (dispatch: Function, getState: Function) => {
    const {
        list: { [ListTypes.MYSHOWS_FILTER]: filterList, [ListTypes.MYSHOWS_ITEMS]: itemList },
        screens: { screenTaskController },
    } = getState();

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyRightPcPinOverlay());
    else if (filterList.isFocused && itemList.limit > 0) {
        dispatch(onKeyRight());
    }
};

export const onKeyLeftMyShows = () => (dispatch: Function, getState: Function) => {
    const {
        list: { [ListTypes.MYSHOWS_ITEMS]: itemList },
        screens: { screenTaskController },
    } = getState();

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyLeftPcPinOverlay());
    else if (itemList.isFocused) {
        dispatch(onKeyLeft());
    }
};

export const onKeyPlayMyShows = () => async (dispatch: Function, getState: Function) => {
    const {
        myShows: {
            myShowsItems: { myShows },
        },
        list: { [ListTypes.MYSHOWS_ITEMS]: itemList },
    } = getState() as RootState;

    if (itemList.isFocused) {
        const currentItem = myShows[itemList.position];
        const isPlayable = currentItem.quickActions?.includes(QuickAction.Play);

        if (!isPlayable) return;

        const services = new ServicesClientImpl();
        let actions: TActionList;

        KeyManager.getInstance().startKeyIgnoring();

        try {
            if (currentItem.unifiedCollectionId) {
                actions = await services.getUnifiedCollectionActions({
                    unifiedCollectionId: currentItem.unifiedCollectionId!,
                    groups: [ActionGroup.Watch],
                    screenType: 'MyShows',
                });
            } else if (currentItem.recordingId) {
                actions = await services.getRecordingActionsV2({
                    recordingId: currentItem.recordingId,
                    groups: [ActionGroup.Watch],
                    screenType: 'MyShows',
                });
            } else {
                Log.e(TAG, 'Cannot play item as unifiedCollectionId and recordingId are undefined');
                KeyManager.getInstance().stopKeyIgnoring();
                return;
            }
        } catch (_error) {
            const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
            Log.e(TAG, errorMessage);
            Diagnostics.error(TAG, errorMessage);
            KeyManager.getInstance().stopKeyIgnoring();
            return;
        }

        const actionWithMyshowActionKernel = actions.actionList.actions.find(
            (item) =>
                item.kernel.actionKernelType === ActionKernelType.WatchFromMyShowsActionKernel ||
                item.kernel.actionKernelType === ActionKernelType.ResumeFromMyShowsActionKernel,
        );

        if (actionWithMyshowActionKernel === undefined) {
            Log.e(TAG, 'Cannot play item as both MyShowsActionKernel  missing in actionList');
            Diagnostics.error(
                TAG,
                'Cannot play item as both MyShowsActionKernel  missing in actionList',
            );
            KeyManager.getInstance().stopKeyIgnoring();
            return;
        }

        const { internalRatingList = [], contentType } =
            actionWithMyshowActionKernel.kernel as unknown as
                | WatchFromMyShowsActionKernel
                | ResumeFromMyShowsActionKernel;

        const isRestricted = services.checkPCViolationsWithRatings(
            internalRatingList,
            currentItem.attribute.includes(MyShowsItemAttributeType.IsAdult),
            contentType,
        );

        if (isRestricted) {
            const ratingName = services.getRestrictedRatingName(internalRatingList);

            dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, false));
            // pc pin challenge
            dispatch(
                showPCPinOverlayWithProgramRating(
                    () => triggerPlayBackForMyShowsItem(currentItem),
                    currentItem.attribute.includes(MyShowsItemAttributeType.IsAdult),
                    ratingName,
                ),
            );
        } else {
            dispatch(triggerPlayBackForMyShowsItem(currentItem));
        }
    }
};

const transitionToSeriesOrProgramScreen =
    (focusedItem: MyShowItemType, selectedFilter: string) => (dispatch: Function) => {
        const { collectionType } = focusedItem;
        const isNonFolderRecordingInPausedFilter =
            focusedItem.myShowsItemId.startsWith('tivo:crc.') &&
            !focusedItem.myShowsType.includes('Folder') &&
            selectedFilter === 'paused';

        if (
            collectionType === CollectionType.Movie ||
            collectionType === CollectionType.Special ||
            isNonFolderRecordingInPausedFilter
        ) {
            let id = focusedItem.myShowsItemId;
            let type = ContentType.RECORDING_ID;

            if (focusedItem.recordingId) {
                id = focusedItem.recordingId;
                type = ContentType.RECORDING_ID;
            } else if (focusedItem.contentId) {
                id = focusedItem.contentId;
                type = ContentType.CONTENT_ID;
            }

            dispatch(programScreenTransition(id, type, showWhisper));
        } else if (focusedItem.unifiedCollectionId && collectionType === CollectionType.Series) {
            dispatch(
                collectionScreenTransition(
                    focusedItem.unifiedCollectionId,
                    TransitionScreenType.MYSHOWS,
                    showWhisper,
                ),
            );
        } else {
            Log.e(TAG, 'Cannot transition to next screen.');
            Diagnostics.error(TAG, 'Cannot transition to next screen.');
        }
    };

export const onKeyOkMyShows = () => async (dispatch: Function, getState: Function) => {
    const {
        list: { [ListTypes.MYSHOWS_FILTER]: filterList, [ListTypes.MYSHOWS_ITEMS]: itemList },
        myShows: { myShowsItems, myShowsFilter },
        screens: { screenTaskController },
    } = getState() as RootState;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyOkPcPinOverlay());
    } else if (filterList.isFocused && itemList.limit > 0) {
        dispatch(onKeyRight());
    } else if (itemList.isFocused) {
        const focusedItem = myShowsItems.myShows[itemList.position];
        const selectedFilter = myShowsFilter[filterList.position].value;

        KeyManager.getInstance().startKeyIgnoring();

        await dispatch(setLastSelectedItem());

        const services = new ServicesClientImpl();
        const isAdultHidden = services.shouldHideMetadata();

        if (focusedItem.attribute.includes(MyShowsItemAttributeType.IsAdult) && isAdultHidden) {
            dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, false));
            // pc pin challenge
            dispatch(
                showPCPinOverlayWithProgramRating(
                    () => transitionToSeriesOrProgramScreen(focusedItem, selectedFilter),
                    focusedItem.attribute.includes(MyShowsItemAttributeType.IsAdult),
                ),
            );
        } else {
            dispatch(transitionToSeriesOrProgramScreen(focusedItem, selectedFilter));
        }
    }
};

export const onKeyDigitMyShows = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyDigitPcPinOverlay());
};

export const onKeyChannelUpMyShows = () => async (dispatch: Function, getState: TGetState) => {
    const {
        list: { [ListTypes.MYSHOWS_FILTER]: filterList },
        myShows: { page, myShowsItems, isPageLoading },
        screens: { screenTaskController },
    } = getState();

    if (
        isPageLoading ||
        page === 0 ||
        filterList.isFocused ||
        screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK
    )
        return;

    dispatch(setMyShowsPageLoadingAction(true));
    MyShowsListGenerator.setCurrentPage(page - 1);
    const highLightIndex = getItemsListHiglightPosition('up', myShowsItems.totalMyShowsCount);
    await dispatch(getMyShowsItems());
    dispatch(setListEntryPosition(ListTypes.MYSHOWS_ITEMS, highLightIndex));
    dispatch(setMyShowsPageLoadingAction(false));
};

export const onKeyChannelDownMyShows = () => async (dispatch: Function, getState: TGetState) => {
    const {
        list: { [ListTypes.MYSHOWS_FILTER]: filterList },
        myShows: { page, myShowsItems, isPageLoading },
        screens: { screenTaskController },
    } = getState();

    if (
        isPageLoading ||
        filterList.isFocused ||
        myShowsItems.totalMyShowsCount <= (page + 1) * MAX_ITEM_IN_PAGE ||
        screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK
    )
        return;

    dispatch(setMyShowsPageLoadingAction(true));
    MyShowsListGenerator.setCurrentPage(page + 1);
    const highLightIndex = getItemsListHiglightPosition('down', myShowsItems.totalMyShowsCount);
    await dispatch(getMyShowsItems());
    dispatch(setListEntryPosition(ListTypes.MYSHOWS_ITEMS, highLightIndex));
    dispatch(setMyShowsPageLoadingAction(false));
};

// Mouse actions
export const onMouseOverMyShows = () => async (dispatch: Function, getState: TGetState) => {
    const {
        myShows: { page, myShowsItems, hasError },
        inputFactory: { value },
        screens: { screenTaskController },
    } = getState();

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onMouseOverPcPinOverlay());
        return;
    }

    if (value.includes('filters')) {
        if (hasError) dispatch(setMyShowsErrorAction(false, ''));
        dispatch(setMyShowsItemsLoadingAction(true));
        await dispatch(setLastSelectedItem());
        dispatch(onMouseOverList());
        dispatch(setMyShowsItemsAction(myShowsItemsObj, 0));
        MyShowsListGenerator.reset();
        dispatch(getMyShowsItems(true));
    } else if (value.includes('items')) {
        dispatch(onMouseOverList());
    } else if (value === InteractiveComponents.NORTH) {
        dispatch(setActiveInteractiveComponent(value));
        if (page) dispatch(setMyShowsCaretVisibleAction(true));
    } else if (value === InteractiveComponents.SOUTH) {
        dispatch(setActiveInteractiveComponent(value));
        if (myShowsItems.totalMyShowsCount > (page + 1) * MAX_ITEM_IN_PAGE)
            dispatch(setMyShowsCaretVisibleAction(true));
    }
};

export const onMouseOutMyShows = () => (dispatch: Function, getState: Function) => {
    const {
        inputFactory: { value },
        screens: { screenTaskController },
    } = getState() as RootState;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onMouseOutPcPinOverlay());
    } else if (value === InteractiveComponents.NORTH || value === InteractiveComponents.SOUTH) {
        dispatch(setMyShowsCaretVisibleAction(false));
    } else if (value.includes('filters') || value.includes('items')) {
        dispatch(onMouseOutList());
    }
};

export const onMouseWheelMyShows =
    (wheelDeltaY: number) => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
            return; // no action needed
        }

        if (wheelDeltaY > 0) {
            dispatch(onKeyUpMyShowsOperations());
        } else if (wheelDeltaY < 0) {
            dispatch(onKeyDownMyShowsOperation());
        }
    };

export const onMouseClickMyShows = () => async (dispatch: Function, getState: Function) => {
    const {
        myShows: { page, myShowsItems, isPageLoading, isCaretVisible },
        inputFactory: { value },
        screens: { screenTaskController },
    } = getState() as RootState;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onMouseClickPcPinOverlay());
        return;
    }

    if (value.includes('myshows-items')) {
        dispatch(onKeyOkMyShows());
    }

    if (!isCaretVisible || isPageLoading) return;

    if (value === InteractiveComponents.NORTH) {
        dispatch(onKeyChannelUpMyShows());
        if (page - 1 === 0) dispatch(setMyShowsCaretVisibleAction(false));
    } else if (value === InteractiveComponents.SOUTH) {
        dispatch(onKeyChannelDownMyShows());
        if (myShowsItems.totalMyShowsCount <= (page + 2) * MAX_ITEM_IN_PAGE)
            dispatch(setMyShowsCaretVisibleAction(false));
    }
};
